import React, { useState, useEffect } from 'react';
import {
  Drawer,
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Divider,
  Avatar,
  Typography,
  IconButton,
  Menu as MenuIcon,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { styled } from '@mui/material/styles';
import PeopleIcon from '@mui/icons-material/People';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import BarChartIcon from '@mui/icons-material/BarChart';
import LogoutIcon from '@mui/icons-material/Logout';
import CampaignIcon from '@mui/icons-material/Campaign';
import PaymentIcon from '@mui/icons-material/Payment';
import FeedbackIcon from '@mui/icons-material/Feedback';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import ArticleIcon from '@mui/icons-material/Article';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import { useNavigate } from 'react-router-dom';
import apiService from '../services/api';
import SessionExpiredDialog from './SessionExpiredDialog';

const DRAWER_WIDTH = 220;

const MenuItemContainer = styled(ListItem)(({ theme, active }) => ({
  margin: theme.spacing(0.5, 1),
  borderRadius: theme.spacing(1),
  cursor: 'pointer',
  backgroundColor: active ? 'rgba(0, 255, 159, 0.12)' : 'transparent',
  '&:hover': {
    backgroundColor: 'rgba(0, 255, 159, 0.2)',
  },
}));

const AdminSidebar = ({ activeTab, setActiveTab }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [sessionExpiredDialogOpen, setSessionExpiredDialogOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const handleSessionExpired = () => setSessionExpiredDialogOpen(true);
    window.addEventListener('sessionExpired', handleSessionExpired);
    return () => window.removeEventListener('sessionExpired', handleSessionExpired);
  }, []);

  const handleLogout = async () => {
    try {
      await apiService.logout();
    } catch (error) {
      console.error('登出失败:', error);
    }
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
    localStorage.removeItem('sessionId');
    navigate('/');
  };

  const handleMenuClick = (item) => {
    setActiveTab(item.id);
    if (isMobile) setMobileOpen(false);
  };

  const menuItems = [
    { id: 'stats', icon: <BarChartIcon />, text: '系统统计' },
    { id: 'users', icon: <PeopleIcon />, text: '用户管理' },
    { id: 'cardkeys', icon: <VpnKeyIcon />, text: '卡密管理' },
    { id: 'packages', icon: <LocalOfferIcon />, text: '充值套餐' },
    { id: 'articles', icon: <ArticleIcon />, text: '文章管理' },
    { id: 'announcements', icon: <CampaignIcon />, text: '公告管理' },
    { id: 'recharge-config', icon: <PaymentIcon />, text: '充值配置' },
    { id: 'feedback', icon: <FeedbackIcon />, text: '反馈管理' },
    { id: 'assist-ai-quota', icon: <AutoAwesomeIcon />, text: '辅助创作' },
    { id: 'reverse-pool', icon: <SwapHorizIcon />, text: '逆向号池' },
    { id: 'floating-widgets', icon: <QrCode2Icon />, text: '悬浮窗配置' },
  ];

  const drawerContent = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: 'rgba(6, 21, 44, 0.98)' }}>
      <Toolbar />
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 2 }}>
        <Avatar sx={{
          width: 52, height: 52, backgroundColor: '#00ff9f', color: '#001e3c',
          fontSize: '1.3rem', fontWeight: 'bold', mb: 1, border: '2px solid rgba(0, 255, 159, 0.4)'
        }}>A</Avatar>
        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#e4f1ff' }}>管理员</Typography>
        <Typography variant="caption" sx={{ color: 'rgba(214,227,252,0.45)' }}>系统管理员</Typography>
      </Box>
      <Divider sx={{ borderColor: 'rgba(0, 255, 159, 0.1)' }} />
      <List sx={{ flex: 1, pt: 1 }}>
        {menuItems.map((item) => (
          <MenuItemContainer
            button
            key={item.id}
            active={activeTab === item.id ? 1 : 0}
            onClick={() => handleMenuClick(item)}
          >
            <ListItemIcon sx={{ color: activeTab === item.id ? '#00ff9f' : 'rgba(214,227,252,0.5)', minWidth: 36 }}>
              {item.icon}
            </ListItemIcon>
            <ListItemText
              primary={item.text}
              primaryTypographyProps={{
                fontWeight: activeTab === item.id ? 'bold' : 'normal',
                color: activeTab === item.id ? '#00ff9f' : 'rgba(214,227,252,0.75)',
                fontSize: '0.85rem'
              }}
            />
          </MenuItemContainer>
        ))}
      </List>
      <Divider sx={{ borderColor: 'rgba(0, 255, 159, 0.1)' }} />
      <List sx={{ pb: 2 }}>
        <MenuItemContainer button onClick={handleLogout}>
          <ListItemIcon sx={{ color: 'rgba(214,227,252,0.4)', minWidth: 36 }}><LogoutIcon /></ListItemIcon>
          <ListItemText primary="退出登录" primaryTypographyProps={{ color: 'rgba(214,227,252,0.55)', fontSize: '0.85rem' }} />
        </MenuItemContainer>
      </List>
    </Box>
  );

  return (
    <>
      {/* 移动端顶部工具栏 */}
      <Box
        sx={{
          display: { xs: 'flex', lg: 'none' },
          alignItems: 'center',
          px: 2,
          height: 56,
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 1201,
          bgcolor: 'rgba(6, 21, 44, 0.98)',
          borderBottom: '1px solid rgba(0, 255, 159, 0.2)',
        }}
      >
        <IconButton onClick={() => setMobileOpen(true)} sx={{ color: '#00ff9f' }}>
          <MenuIcon />
        </IconButton>
        <Typography variant="h6" sx={{ color: '#00ff9f', fontWeight: 'bold', flex: 1 }}>
          {menuItems.find(m => m.id === activeTab)?.text || '管理后台'}
        </Typography>
      </Box>

      {/* 移动端临时抽屉 - 直接渲染，不用sx控制显示 */}
      {isMobile && (
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{
            '& .MuiDrawer-paper': {
              width: DRAWER_WIDTH,
              boxSizing: 'border-box',
              bgcolor: 'rgba(6, 21, 44, 0.98)',
              borderRight: '1px solid rgba(0, 255, 159, 0.2)',
            },
          }}
        >
          {drawerContent}
        </Drawer>
      )}

      {/* 桌面端永久抽屉 - 直接渲染，不用sx控制显示 */}
      {!isMobile && (
        <Drawer
          variant="permanent"
          sx={{
            '& .MuiDrawer-paper': {
              width: DRAWER_WIDTH,
              boxSizing: 'border-box',
              bgcolor: 'rgba(6, 21, 44, 0.98)',
              borderRight: '1px solid rgba(0, 255, 159, 0.2)',
            },
          }}
        >
          {drawerContent}
        </Drawer>
      )}

      <SessionExpiredDialog open={sessionExpiredDialogOpen} onClose={() => setSessionExpiredDialogOpen(false)} />
    </>
  );
};

export default AdminSidebar;
